/**
 * Customer Conversations Tab.
 *
 * Lists Andrei chat sessions for the current customer (rows from
 * `andrei_sessions` where `customerId === client.id`) and offers a
 * "New conversation" button. Clicking a row opens the Andrei panel
 * with that session id loaded, so all reads + writes from then on
 * stay scoped to that thread.
 *
 * This component replaces the per-customer view that used to live in
 * the legacy [chat] drawer's GeminiChatInterface. We use Andrei's
 * floating panel as the chat surface rather than embedding a chat UI
 * inline — keeps one chat engine instead of two.
 *
 * Data model:
 *   - Sessions doc: andrei_sessions/{sessionId}
 *     { customerId, jobId?, title, lastUpdatedAt, lastMessagePreview, createdAt }
 *   - Messages: andrei_messages with { sessionId, customerId via context }
 *
 * Sessions are auto-titled by the trigger from the first user message
 * when title is blank. The user can rename inline (TODO future phase).
 */

import React, { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { useAndreiChat } from '../../../contexts/AndreiChatContext';
import { deleteConversation } from '../../../utils/andreiConversations';
import ConfirmModal from '../../../components/ConfirmModal';

interface SessionRow {
  id: string;
  customerId?: string;
  jobId?: string;
  title?: string;
  lastMessagePreview?: string;
  lastUpdatedAt?: { toDate?: () => Date } | null;
  createdAt?: { toDate?: () => Date } | null;
}

interface Props {
  customerId: string;
  customerName?: string;
}

function formatRelative(d?: Date | null): string {
  if (!d) return '';
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return d.toLocaleDateString();
}

export function ClientConversationsTab({ customerId, customerName }: Props) {
  const { openAndreiChat, sessionId: activeSessionId, setSessionId } = useAndreiChat();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<SessionRow | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [deleting, setDeleting] = useState<SessionRow | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  useEffect(() => {
    if (!customerId) { setSessions([]); setLoading(false); return; }
    // Per-user scoping: sessions are owned by whoever started them (rules
    // enforce it — a query that could return another user's docs is
    // rejected outright, so the userUid constraint is mandatory).
    const uid = getAuth().currentUser?.uid;
    if (!uid) { setSessions([]); setLoading(false); return; }
    const db = getFirestore();
    const q = query(
      collection(db, 'andrei_sessions'),
      where('customerId', '==', customerId),
      where('userUid', '==', uid),
      orderBy('lastUpdatedAt', 'desc'),
    );
    setError(null);
    const unsub = onSnapshot(q, (snap) => {
      setSessions(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setError(null);
      setLoading(false);
    }, (err) => {
      // Most likely a missing index — the orderBy + where pair needs
      // a composite index (now declared in firestore.indexes.json). Surface
      // it as a DISTINCT error state so the user doesn't read a load failure
      // as "no conversations yet" and lose access to real threads.
      console.error('[ClientConversationsTab] subscribe failed:', err);
      setError(err?.message || 'Could not load conversations.');
      setLoading(false);
    });
    return () => unsub();
  }, [customerId]);

  const handleNew = async () => {
    if (!customerId) return;
    setCreating(true);
    try {
      const db = getFirestore();
      const sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      // Pre-create the session doc so it shows up in the list
      // immediately, even before the user sends a first message. The
      // trigger will fill in title + lastMessagePreview on the first
      // message; until then it renders as "(new conversation)".
      await setDoc(doc(db, 'andrei_sessions', sessionId), {
        id: sessionId,
        customerId,
        // Session owner — required by rules (create is denied without it)
        // and by the per-user list queries above.
        userUid: getAuth().currentUser?.uid ?? null,
        title: '',
        createdAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
        lastMessagePreview: '',
      });
      openAndreiChat({
        context: { type: 'customer', id: customerId, label: customerName || customerId },
        sessionId,
      });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to start conversation');
    } finally {
      setCreating(false);
    }
  };

  const handleOpen = (s: SessionRow) => {
    openAndreiChat({
      context: { type: 'customer', id: customerId, label: customerName || customerId },
      sessionId: s.id,
    });
  };

  const openRename = (s: SessionRow) => {
    setRenaming(s);
    setRenameValue((s.title || '').trim());
  };
  const submitRename = async () => {
    if (!renaming) return;
    const title = renameValue.trim();
    if (!title || title === (renaming.title || '').trim()) { setRenaming(null); return; }
    setSavingRename(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'andrei_sessions', renaming.id), { title });
      toast.success('Conversation renamed');
      setRenaming(null);
    } catch (e: any) {
      toast.error(e?.message || 'Could not rename conversation');
    } finally {
      setSavingRename(false);
    }
  };

  const submitDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      // Server-side THOROUGH delete (session doc + all messages + compaction
      // backups). Deleting only the session doc here used to orphan every
      // message invisibly forever.
      const r = await deleteConversation(deleting.id);
      // If this thread is loaded in the Andrei panel, drop it — otherwise the
      // next send recreates the session doc and resurrects the conversation.
      if (activeSessionId === deleting.id) setSessionId(null);
      toast.success(`Deleted conversation${r.deletedMessages ? ` + ${r.deletedMessages} messages` : ''}`);
      setDeleting(null);
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete conversation');
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <>
    <div className="card">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          Conversations
          <span className="text-muted ms-2 small">({sessions.length})</span>
        </h6>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleNew}
          disabled={creating}
        >
          <i className="bi bi-chat-square-text me-2" />
          {creating ? 'Starting…' : 'New conversation'}
        </button>
      </div>
      <div className="card-body p-0">
        {loading ? (
          <div className="p-4 text-muted">Loading…</div>
        ) : error ? (
          <div className="p-5 text-center text-danger">
            <i className="bi bi-exclamation-triangle fs-1 d-block mb-2" />
            Couldn’t load conversations.<br />
            <span className="small text-muted">{error}</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-5 text-center text-muted">
            <i className="bi bi-chat-square-dots fs-1 d-block mb-2 text-gray-500" />
            No conversations yet for this customer.<br />
            <span className="small">Click <strong>New conversation</strong> to start one with Andrei scoped to this customer.</span>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle table-row-dashed table-hover gy-3 mb-0">
              <thead>
                <tr className="fw-semibold text-muted fs-8 text-uppercase">
                  <th className="ps-4">Conversation</th>
                  <th>Last message</th>
                  <th style={{ width: 110 }}>Updated</th>
                  <th style={{ width: 160 }} className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const title = (s.title || '').trim() || '(new conversation)';
                  const preview = (s.lastMessagePreview || '').trim() || '—';
                  const updatedDate = s.lastUpdatedAt?.toDate ? s.lastUpdatedAt.toDate() : null;
                  return (
                    <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => handleOpen(s)}>
                      <td className="ps-4">
                        <div className="fw-semibold text-gray-800 text-truncate" style={{ maxWidth: 360 }}>{title}</div>
                      </td>
                      <td className="text-muted text-truncate" style={{ maxWidth: 320 }}>{preview}</td>
                      <td className="text-muted small text-nowrap">{formatRelative(updatedDate)}</td>
                      <td className="text-end pe-4">
                        <div className="d-inline-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="btn btn-sm btn-icon btn-light" title="Rename" onClick={() => openRename(s)}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button type="button" className="btn btn-sm btn-icon btn-light" title="Delete" onClick={() => setDeleting(s)}>
                            <i className="bi bi-trash" />
                          </button>
                          <button type="button" className="btn btn-sm btn-light" onClick={() => handleOpen(s)}>Open</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

    <ConfirmModal
      show={!!renaming}
      title="Rename conversation"
      variant="primary"
      confirmLabel="Save"
      busy={savingRename}
      confirmDisabled={!renameValue.trim()}
      body={
        <>
          <label className="form-label fs-7 text-muted">Conversation title</label>
          <input
            type="text"
            className="form-control"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
            maxLength={120}
            autoFocus
            placeholder="e.g. Roof rack quote follow-up"
          />
        </>
      }
      onConfirm={submitRename}
      onCancel={() => setRenaming(null)}
    />
    <ConfirmModal
      show={!!deleting}
      title="Delete conversation?"
      variant="danger"
      confirmLabel="Delete"
      busy={deletingBusy}
      body={<>Permanently delete <strong>{(deleting?.title || '').trim() || 'this conversation'}</strong>? This removes the thread, all of its messages, and its compaction backups. It can’t be undone.</>}
      onConfirm={submitDelete}
      onCancel={() => setDeleting(null)}
    />
    </>
  );
}

export default ClientConversationsTab;
